import { ForwardRefWithStaticComponents } from '@vision/ui/interfaces';
import { HoverCard, HoverCardProps, MantineSize } from '@mantine/core';
import { TooltipCardContainer, TooltipCardContainerProps } from './TooltipCardContainer';
import classes from './TooltipCard.module.css';
import clsx from 'clsx';
import React, { forwardRef, useEffect, useState } from 'react';

interface TooltipCardProps extends HoverCardProps {
  className?: string;
  id?: string;
  variant?: 'default' | 'light';
  width?: number;
  withArrow?: boolean;
  withinAnimation?: boolean;
  zIndex?: number;
  offset?: number;
  openDelay?: number;
}

const _TooltipCard = forwardRef<HTMLDivElement, React.PropsWithChildren<TooltipCardProps>>(
  (
    { children, width, position, disabled, withinAnimation, className, zIndex, withArrow, offset, openDelay, ...props },
    ref,
  ) => {
    const [activeAnimation, setActiveAnimation] = useState(withinAnimation || false);
    const [shouldClose, setShouldClose] = useState(false);

    useEffect(() => {
      if (withinAnimation) {
        setActiveAnimation(true);
      }
    }, [withinAnimation]);

    useEffect(() => {
      if (shouldClose) {
        setActiveAnimation(false);
        setShouldClose(false);
      }
    }, [shouldClose]);

    const handleClose = () => {
      setShouldClose(true);
    };

    return (
      <div ref={ref} {...props} className={className}>
        <HoverCard
          withArrow={withArrow}
          arrowRadius={3}
          arrowSize={10}
          arrowOffset={15}
          position={position}
          offset={offset}
          zIndex={zIndex}
          disabled={disabled}
          keepMounted={withinAnimation}
          withinPortal={true}
          width={width}
          initiallyOpened={withinAnimation}
          openDelay={openDelay}
          classNames={{
            arrow: classes.tooltipCardArrow,
            dropdown: clsx({ [classes.shakeAnimation]: activeAnimation }),
          }}
          onClose={handleClose}
        >
          {children}
        </HoverCard>
      </div>
    );
  },
) as any;

function TooltipCardTarget({ children }: React.PropsWithChildren) {
  return <HoverCard.Target>{children}</HoverCard.Target>;
}

function TooltipCardDropdown({
  accentColor,
  children,
  className,
  variant,
  size = 'md',
}: React.PropsWithChildren<TooltipCardContainerProps & { variant?: 'default' | 'light'; size?: MantineSize }>) {
  return (
    <HoverCard.Dropdown
      className={clsx(classes.tooltipCardDropdown, className, {
        [classes.tooltipCardDropdownLight]: variant === 'light',
      })}
    >
      <TooltipCardContainer variant={variant} size={size} accentColor={accentColor}>
        {children}
      </TooltipCardContainer>
    </HoverCard.Dropdown>
  );
}

_TooltipCard.Target = TooltipCardTarget;
_TooltipCard.Dropdown = TooltipCardDropdown;

export const TooltipCard: ForwardRefWithStaticComponents<
  HTMLDivElement,
  TooltipCardProps,
  { Target: typeof TooltipCardTarget; Dropdown: typeof TooltipCardDropdown }
> = _TooltipCard;
